import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { talleresAPI, fabricantesAPI, paisesAPI } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Users, Eye, Loader2, Ban, Upload, X, Send, CreditCard } from 'lucide-react';
import { formatApiError } from '../../utils/apiError';

// Helper para ordenar talleres por número ANAC
const sortTalleresByAnac = (talleres) => {
  return [...talleres].sort((a, b) => {
    const anacA = a.numero_anac || '';
    const anacB = b.numero_anac || '';
    return anacA.localeCompare(anacB, undefined, { numeric: true, sensitivity: 'base' });
  });
};

const AdminTalleres = () => {
  const navigate = useNavigate();
  const irAEnviarMensaje = (taller) => {
    const nombre = encodeURIComponent(taller?.razon_social || '');
    navigate(`/admin/suscripciones?activeTab=notificaciones&taller_ids=${taller.id}&taller_nombre=${nombre}`);
  };
  const [talleres, setTalleres] = useState([]);
  const [fabricantes, setFabricantes] = useState([]);
  const [paises, setPaises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPais, setFilterPais] = useState('');
  const [filterNivel, setFilterNivel] = useState('');
  const [selectedTaller, setSelectedTaller] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // Para el selector de modelos multi-fabricante
  const [modeloSelectorFab, setModeloSelectorFab] = useState('');
  const [modeloSelectorModelos, setModeloSelectorModelos] = useState([]);
  const [capacidadesSeleccionadas, setCapacidadesSeleccionadas] = useState([]); // {id, nombre, fabricante_nombre}
  
  const [formData, setFormData] = useState({
    numero_anac: '',
    razon_social: '',
    pais: 'Argentina',
    provincia: '',
    direccion: '',
    telefono: '',
    email: '',
    nivel_suscripcion: 'status_report',
    capacidades_anac: [],
    notas: '',
    logo_url: '',
    sitio_web: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    loadTalleres();
    loadFabricantes();
    loadPaises();
  }, [filterPais, filterNivel]);

  const loadPaises = async () => {
    try {
      const r = await paisesAPI.getActivos();
      setPaises(r.data.items || []);
    } catch (e) {
      console.error('Error cargando países:', e);
    }
  };

  // Helpers para resolver provincias y bandera por país
  const getProvinciasPorPais = (paisNombre) => {
    const p = paises.find(x => x.nombre === paisNombre);
    return p?.provincias || [];
  };
  const getPaisByNombre = (paisNombre) => paises.find(x => x.nombre === paisNombre);

  const loadFabricantes = async () => {
    try {
      const response = await fabricantesAPI.getAll();
      setFabricantes(response.data);
    } catch (error) {
      console.error('Error cargando fabricantes:', error);
    }
  };

  const loadTalleres = async () => {
    try {
      const params = { limit: 500 }; // Cargar todos los talleres
      if (filterPais && filterPais !== 'all') params.pais = filterPais;
      if (filterNivel && filterNivel !== 'all') params.nivel_suscripcion = filterNivel;
      
      const response = await talleresAPI.getAll(params);
      // Manejar respuesta paginada o array directo y ordenar por ANAC
      const data = response.data?.data || response.data || [];
      setTalleres(sortTalleresByAnac(Array.isArray(data) ? data : []));
    } catch (error) {
      toast.error('Error al cargar talleres');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      numero_anac: '',
      razon_social: '',
      pais: 'Argentina',
      provincia: '',
      direccion: '',
      telefono: '',
      email: '',
      nivel_suscripcion: 'sin_suscripcion',
      capacidades_anac: [],
      notas: '',
      logo_url: '',
      sitio_web: ''
    });
    setLogoFile(null);
    setLogoPreview(null);
    setEditMode(false);
    setModeloSelectorFab('');
    setModeloSelectorModelos([]);
    setCapacidadesSeleccionadas([]);
  };

  const openNewForm = () => {
    resetForm();
    setShowForm(true);
  };

  const openEditForm = async (taller) => {
    // Primero abrir el dialog inmediatamente
    setSelectedTaller(taller);
    setEditMode(true);
    setShowForm(true);
    
    // Setear datos básicos
    setFormData({
      numero_anac: taller.numero_anac || '',
      razon_social: taller.razon_social || '',
      pais: taller.pais || taller.ubicacion?.pais || 'Argentina',
      provincia: taller.provincia || taller.ubicacion?.provincia || '',
      direccion: taller.titular_suscripcion?.direccion || taller.direccion || '',
      telefono: taller.titular_suscripcion?.telefono || taller.telefono || '',
      email: taller.titular_suscripcion?.email || taller.email || '',
      nivel_suscripcion: taller.nivel_suscripcion || 'status_report',
      capacidades_anac: Array.isArray(taller.capacidades_anac) ? taller.capacidades_anac : [],
      notas: taller.notas || '',
      logo_url: taller.logo_url || '',
      sitio_web: taller.sitio_web || ''
    });
    setLogoPreview(taller.logo_url || null);
    setLogoFile(null);
    
    // Cargar capacidades existentes - OPTIMIZADO para evitar cientos de llamadas API
    if (Array.isArray(taller.capacidades_anac) && taller.capacidades_anac.length > 0) {
      try {
        // Primero, cargar TODOS los modelos de TODOS los fabricantes de una vez
        const todosLosModelos = {};
        const fabricantesPorId = {};
        
        // Cargar modelos de cada fabricante en paralelo
        const promesas = fabricantes.map(async (fab) => {
          try {
            const res = await fabricantesAPI.getModelos(fab.id);
            return { fabricante: fab, modelos: res.data || [] };
          } catch (e) {
            return { fabricante: fab, modelos: [] };
          }
        });
        
        const resultados = await Promise.all(promesas);
        
        // Indexar todos los modelos por ID
        for (const { fabricante, modelos } of resultados) {
          fabricantesPorId[fabricante.id] = fabricante;
          for (const modelo of modelos) {
            todosLosModelos[modelo.id] = {
              ...modelo,
              fabricante_id: fabricante.id,
              fabricante_nombre: fabricante.nombre
            };
          }
        }
        
        // Ahora resolver cada capacidad del taller usando el índice
        const capacidadesInfo = [];
        for (const modeloId of taller.capacidades_anac) {
          if (todosLosModelos[modeloId]) {
            const modelo = todosLosModelos[modeloId];
            capacidadesInfo.push({
              id: modelo.id,
              nombre: modelo.nombre,
              fabricante_id: modelo.fabricante_id,
              fabricante_nombre: modelo.fabricante_nombre
            });
          } else {
            // Intentar extraer info del ID (formato: fabricante_id_nombre)
            const partes = modeloId.split('_');
            let nombreModelo = modeloId;
            let fabNombre = '(Desconocido)';
            
            if (partes.length >= 2) {
              const fabId = partes[0];
              nombreModelo = partes.slice(1).join('_');
              if (fabricantesPorId[fabId]) {
                fabNombre = fabricantesPorId[fabId].nombre;
              }
            }
            
            capacidadesInfo.push({
              id: modeloId,
              nombre: nombreModelo,
              fabricante_id: partes[0] || '',
              fabricante_nombre: fabNombre
            });
          }
        }
        
        setCapacidadesSeleccionadas(capacidadesInfo);
        console.log(`Cargadas ${capacidadesInfo.length} capacidades para ${taller.razon_social}`);
      } catch (e) {
        console.error('Error cargando capacidades:', e);
        // FALLBACK: Parsear los IDs para mostrar algo legible
        const fallbackCapacidades = taller.capacidades_anac.map(id => {
          const partes = id.split('_');
          const nombreModelo = partes.length >= 2 ? partes.slice(1).join('_') : id;
          return {
            id: id,
            nombre: nombreModelo,
            fabricante_id: partes[0] || '',
            fabricante_nombre: '(Error al cargar)'
          };
        });
        setCapacidadesSeleccionadas(fallbackCapacidades);
      }
    } else {
      setCapacidadesSeleccionadas([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // IMPORTANTE: Usar capacidadesSeleccionadas si hay, sino usar formData.capacidades_anac original
      // Esto previene pérdida de datos si la carga de info de fabricantes falla
      const capacidadesAGuardar = capacidadesSeleccionadas.length > 0 
        ? capacidadesSeleccionadas.map(c => c.id)
        : (formData.capacidades_anac || []);
      
      // Estructurar datos según el modelo del backend
      const data = {
        numero_anac: formData.numero_anac,
        razon_social: formData.razon_social,
        ubicacion: {
          pais: formData.pais,
          provincia: formData.provincia
        },
        nivel_suscripcion: formData.nivel_suscripcion,
        estado_contractual: 'activo', // Default para nuevos talleres
        titular_suscripcion: {
          nombre: formData.razon_social, // Usar razón social como nombre por defecto
          email: formData.email || 'pendiente@taller.com',
          telefono: formData.telefono || '',
          dni: '',
          direccion: formData.direccion || '',
          rol: 'Propietario'
        },
        capacidades_anac: capacidadesAGuardar,
        sitio_web: (formData.sitio_web || '').trim() || null
      };
      
      // Agregar notas si existen
      if (formData.notas) {
        data.informacion_adicional = { notas: formData.notas };
      }

      let tallerId;
      if (editMode && selectedTaller) {
        // Para actualización, enviar todos los campos relevantes
        const updateData = {
          numero_anac: formData.numero_anac,
          razon_social: formData.razon_social,
          ubicacion: {
            pais: formData.pais,
            provincia: formData.provincia
          },
          nivel_suscripcion: formData.nivel_suscripcion,
          capacidades_anac: capacidadesAGuardar,
          sitio_web: (formData.sitio_web || '').trim(),
          // Incluir titular_suscripcion con dirección y teléfono
          titular_suscripcion: {
            nombre: selectedTaller.titular_suscripcion?.nombre || formData.razon_social,
            email: formData.email || selectedTaller.titular_suscripcion?.email || 'pendiente@taller.com',
            telefono: formData.telefono || selectedTaller.titular_suscripcion?.telefono || '',
            dni: selectedTaller.titular_suscripcion?.dni || '',
            direccion: formData.direccion || selectedTaller.titular_suscripcion?.direccion || '',
            rol: selectedTaller.titular_suscripcion?.rol || 'Propietario'
          }
        };
        if (formData.notas) {
          updateData.informacion_adicional = { notas: formData.notas };
        }
        console.log('Actualizando taller con capacidades:', capacidadesAGuardar);
        await talleresAPI.update(selectedTaller.id, updateData);
        tallerId = selectedTaller.id;
        toast.success('Taller actualizado correctamente');
      } else {
        const res = await talleresAPI.create(data);
        tallerId = res.data.id;
        toast.success('Taller creado correctamente');
      }

      // Subir logo si hay uno seleccionado
      if (logoFile && tallerId) {
        try {
          await talleresAPI.uploadLogo(tallerId, logoFile);
          toast.success('Logo subido correctamente');
        } catch (logoError) {
          console.error('Error subiendo logo:', logoError);
          toast.error('El taller se guardó pero hubo un error al subir el logo');
        }
      }

      setShowForm(false);
      resetForm();
      loadTalleres();
    } catch (error) {
      // Manejar errores de validación de Pydantic que vienen como array
      const errorDetail = error.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        const messages = errorDetail.map(e => e.msg || e.message || 'Error de validación').join(', ');
        toast.error(messages);
      } else if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else {
        toast.error('Error al guardar el taller');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (taller) => {
    if (!confirm(`¿Está seguro de eliminar el taller ${taller.razon_social}?`)) return;
    
    try {
      const response = await talleresAPI.delete(taller.id);
      if (response.status === 200) {
        toast.success('Taller eliminado');
        // Actualizar lista localmente antes de recargar
        setTalleres(prev => prev.filter(t => t.id !== taller.id));
        // También recargar del servidor
        await loadTalleres();
      }
    } catch (error) {
      console.error('Error eliminando taller:', error);
      toast.error(formatApiError(error, 'Error al eliminar el taller'));
      // Recargar para sincronizar estado
      await loadTalleres();
    }
  };

  const handleSuspender = async (taller) => {
    const accion = taller.estado_contractual === 'suspendido' ? 'reactivar' : 'suspender';
    const nuevoEstado = taller.estado_contractual === 'suspendido' ? 'activo' : 'suspendido';
    
    if (!confirm(`¿Está seguro de ${accion} el taller ${taller.razon_social}?`)) return;
    
    try {
      await talleresAPI.update(taller.id, { estado_contractual: nuevoEstado });
      toast.success(`Taller ${accion === 'suspender' ? 'suspendido' : 'reactivado'} correctamente`);
      loadTalleres();
    } catch (error) {
      toast.error(`Error al ${accion} el taller`);
    }
  };

  // Cargar modelos para el selector
  const loadModelosSelector = async (fabId) => {
    try {
      const response = await fabricantesAPI.getModelos(fabId);
      setModeloSelectorModelos(response.data);
    } catch (error) {
      setModeloSelectorModelos([]);
    }
  };

  // Agregar modelo a capacidades
  const agregarCapacidad = (modelo) => {
    const fabricante = fabricantes.find(f => f.id === modeloSelectorFab);
    if (!capacidadesSeleccionadas.find(c => c.id === modelo.id)) {
      setCapacidadesSeleccionadas([...capacidadesSeleccionadas, {
        id: modelo.id,
        nombre: modelo.nombre,
        fabricante_nombre: fabricante?.nombre || '',
        fabricante_id: modeloSelectorFab
      }]);
    }
  };

  // Quitar modelo de capacidades
  const quitarCapacidad = (modeloId) => {
    setCapacidadesSeleccionadas(capacidadesSeleccionadas.filter(c => c.id !== modeloId));
  };

  // Orden de prioridad de suscripciones (menor número = mayor prioridad)
  const getNivelPrioridad = (nivel) => {
    switch (nivel) {
      case 'premium': return 1;
      case 'estandar_pro': return 2;
      case 'estandar': return 3;
      case 'status_report': return 4;
      case 'sin_suscripcion': return 5;
      default: return 5;
    }
  };

  const filteredTalleres = talleres
    .filter(t =>
      (t.razon_social?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (t.numero_anac?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      // Primero ordenar por nivel de suscripción (pagas primero)
      const nivelDiff = getNivelPrioridad(a.nivel_suscripcion) - getNivelPrioridad(b.nivel_suscripcion);
      if (nivelDiff !== 0) return nivelDiff;
      // Luego ordenar por número ANAC
      return (a.numero_anac || '').localeCompare(b.numero_anac || '');
    });

  const getNivelBadge = (nivel) => {
    switch (nivel) {
      case 'premium': return <Badge className="bg-amber-500">Premium</Badge>;
      case 'estandar_pro': return <Badge className="bg-sky-500">Standard Pro</Badge>;
      case 'estandar': return <Badge className="bg-teal-500">Standard</Badge>;
      case 'sin_suscripcion': return <Badge variant="outline" className="border-gray-400 text-gray-500">Sin suscripción</Badge>;
      case 'status_report': return <Badge variant="outline">Status Report</Badge>;
      default: return <Badge variant="outline">Status Report</Badge>;
    }
  };

  const getEstadoBadge = (estado) => {
    if (estado === 'suspendido') {
      return <Badge variant="destructive" className="ml-2">Suspendido</Badge>;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto py-6 px-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Gestión de Talleres</h1>
          <Button onClick={openNewForm} className="bg-teal-500 hover:bg-teal-600">
            <Plus className="mr-2 h-4 w-4" /> Nuevo Taller
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid sm:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nombre o ANAC..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterPais} onValueChange={setFilterPais}>
                <SelectTrigger>
                  <SelectValue placeholder="Filtrar por país" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los países</SelectItem>
                  {paises.map(p => (
                    <SelectItem key={p.id} value={p.nombre}>
                      {p.bandera ? `${p.bandera} ` : ''}{p.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterNivel} onValueChange={setFilterNivel}>
                <SelectTrigger>
                  <SelectValue placeholder="Filtrar por nivel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los niveles</SelectItem>
                  <SelectItem value="sin_suscripcion">Sin suscripción</SelectItem>
                  <SelectItem value="status_report">Status Report</SelectItem>
                  <SelectItem value="estandar">Standard</SelectItem>
                  <SelectItem value="estandar_pro">Standard Pro</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => { setFilterPais(''); setFilterNivel(''); setSearchTerm(''); }}>
                Limpiar filtros
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Talleres List */}
        <Card>
          <CardContent className="pt-6">
            {loading ? (
              <p className="text-center py-8">Cargando...</p>
            ) : filteredTalleres.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">No hay talleres registrados</p>
                <Button onClick={openNewForm} className="bg-teal-500 hover:bg-teal-600">
                  <Plus className="mr-2 h-4 w-4" /> Crear primer taller
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Taller</th>
                      <th className="text-left py-3 px-4">ANAC</th>
                      <th className="text-left py-3 px-4">Ubicación</th>
                      <th className="text-left py-3 px-4">Nivel</th>
                      <th className="text-left py-3 px-4">Contacto</th>
                      <th className="text-right py-3 px-4">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTalleres.map(taller => (
                      <tr key={taller.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium">{taller.razon_social}</p>
                            {taller.debito_automatico && (
                              <Badge
                                className="bg-purple-100 text-purple-700 hover:bg-purple-100 text-[10px] px-1.5 py-0 gap-1"
                                title="Adherido a Débito Automático"
                                data-testid={`badge-debito-auto-${taller.id}`}
                              >
                                <CreditCard className="h-3 w-3" />
                                DÉB.AUTO
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 font-mono text-sm">{taller.numero_anac}</td>
                        <td className="py-3 px-4">
                          <p className="text-sm">{taller.provincia || taller.ubicacion?.provincia}</p>
                          <p className="text-xs text-gray-500">{taller.pais || taller.ubicacion?.pais}</p>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1">
                            {getNivelBadge(taller.nivel_suscripcion)}
                            {getEstadoBadge(taller.estado_contractual)}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-sm">{taller.email || taller.titular_suscripcion?.email}</p>
                          <p className="text-xs text-gray-500">{taller.telefono || taller.titular_suscripcion?.telefono}</p>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setSelectedTaller(taller); setShowDetail(true); }}
                              title="Ver detalles"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => openEditForm(taller)}
                              title="Editar"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => irAEnviarMensaje(taller)}
                              title="Enviar mensaje directo a este taller"
                              className="text-teal-600 hover:text-teal-700"
                              data-testid={`talleres-enviar-mensaje-${taller.id}`}
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleSuspender(taller)}
                              title={taller.estado_contractual === 'suspendido' ? 'Reactivar' : 'Suspender por falta de pago'}
                              className={taller.estado_contractual === 'suspendido' ? 'text-green-500 hover:text-green-700' : 'text-amber-500 hover:text-amber-700'}
                            >
                              <Ban className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDelete(taller)}
                              title="Eliminar"
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Form Dialog */}
        <Dialog open={showForm} onOpenChange={(open) => { setShowForm(open); if (!open) resetForm(); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Editar Taller' : 'Nuevo Taller'}</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="numero_anac">Número ANAC *</Label>
                  <Input
                    id="numero_anac"
                    placeholder="Ej: 1B 033"
                    value={formData.numero_anac}
                    onChange={(e) => setFormData({...formData, numero_anac: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="razon_social">Razón Social *</Label>
                  <Input
                    id="razon_social"
                    placeholder="Nombre del taller"
                    value={formData.razon_social}
                    onChange={(e) => setFormData({...formData, razon_social: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>País *</Label>
                  <Select 
                    value={formData.pais} 
                    onValueChange={(value) => setFormData({...formData, pais: value, provincia: ''})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {paises.map(p => (
                        <SelectItem key={p.id} value={p.nombre}>
                          {p.bandera ? `${p.bandera} ` : ''}{p.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Provincia/Estado *</Label>
                  <Select 
                    value={formData.provincia} 
                    onValueChange={(value) => setFormData({...formData, provincia: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      {getProvinciasPorPais(formData.pais).map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="direccion">Dirección</Label>
                <Input
                  id="direccion"
                  placeholder="Dirección completa"
                  value={formData.direccion}
                  onChange={(e) => setFormData({...formData, direccion: e.target.value})}
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="telefono">Teléfono</Label>
                  <Input
                    id="telefono"
                    placeholder="+54 ..."
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="contacto@taller.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sitio_web">Sitio web del taller (opcional)</Label>
                <Input
                  id="sitio_web"
                  type="url"
                  placeholder="https://www.taller.com.ar"
                  value={formData.sitio_web}
                  onChange={(e) => setFormData({...formData, sitio_web: e.target.value})}
                  data-testid="form-sitio-web"
                />
                <p className="text-xs text-gray-500">
                  Si lo cargás, al hacer click sobre el taller en la pestaña pública "Talleres", se abrirá esta URL en una nueva pestaña. Si lo dejás vacío, redirige al login.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Nivel de Suscripción</Label>
                <Select 
                  value={formData.nivel_suscripcion} 
                  onValueChange={(value) => setFormData({...formData, nivel_suscripcion: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar nivel..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sin_suscripcion">Sin suscripción (no visible en web)</SelectItem>
                    <SelectItem value="status_report">Status Report (Gratis)</SelectItem>
                    <SelectItem value="estandar">Standard ($120/mes)</SelectItem>
                    <SelectItem value="estandar_pro">Standard Pro ($220/mes)</SelectItem>
                    <SelectItem value="premium">Premium ($400/mes)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  Los talleres "Sin suscripción" no aparecen en la página pública
                </p>
              </div>

              {/* Selector de Capacidades ANAC - Multi-fabricante */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <Label className="text-base font-medium">Capacidades ANAC</Label>
                <p className="text-xs text-gray-500 mb-3">
                  Selecciona los modelos habilitados para este taller. Puedes agregar modelos de diferentes fabricantes.
                </p>
                
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <Label className="text-sm">Fabricante</Label>
                    <Select 
                      value={modeloSelectorFab} 
                      onValueChange={(v) => {
                        setModeloSelectorFab(v);
                        loadModelosSelector(v);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar fabricante..." />
                      </SelectTrigger>
                      <SelectContent>
                        {fabricantes.map(f => (
                          <SelectItem key={f.id} value={f.id}>{f.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Modelo</Label>
                    <Select 
                      value=""
                      onValueChange={(modeloId) => {
                        const modelo = modeloSelectorModelos.find(m => m.id === modeloId);
                        if (modelo) agregarCapacidad(modelo);
                      }}
                      disabled={!modeloSelectorFab || modeloSelectorModelos.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={modeloSelectorModelos.length === 0 ? "Sin modelos" : "Agregar modelo..."} />
                      </SelectTrigger>
                      <SelectContent>
                        {modeloSelectorModelos.map(m => (
                          <SelectItem key={m.id} value={m.id}>{m.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Checkbox Seleccionar Todos */}
                {modeloSelectorFab && modeloSelectorModelos.length > 0 && (
                  <div className="flex items-center space-x-2 mb-3 p-2 bg-teal-50 rounded border border-teal-200">
                    <Checkbox 
                      id="selectAllCapacidades"
                      onCheckedChange={(checked) => {
                        if (checked && modeloSelectorModelos.length > 0) {
                          const fabricante = fabricantes.find(f => f.id === modeloSelectorFab);
                          const nuevosModelos = modeloSelectorModelos
                            .filter(m => !capacidadesSeleccionadas.find(sel => sel.id === m.id))
                            .map(m => ({
                              id: m.id,
                              nombre: m.nombre,
                              fabricante_nombre: fabricante?.nombre || '',
                              fabricante_id: modeloSelectorFab
                            }));
                          setCapacidadesSeleccionadas([...capacidadesSeleccionadas, ...nuevosModelos]);
                        }
                      }}
                    />
                    <label htmlFor="selectAllCapacidades" className="text-sm font-medium cursor-pointer text-teal-800">
                      Seleccionar todos los modelos de {fabricantes.find(f => f.id === modeloSelectorFab)?.nombre} ({modeloSelectorModelos.length})
                    </label>
                  </div>
                )}

                {/* Lista de capacidades seleccionadas */}
                {capacidadesSeleccionadas.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {capacidadesSeleccionadas.map(c => (
                      <Badge 
                        key={c.id} 
                        variant="secondary" 
                        className="flex items-center gap-1 px-2 py-1"
                      >
                        <span className="text-xs text-gray-500">{c.fabricante_nombre}:</span>
                        <span>{c.nombre}</span>
                        <X 
                          className="h-3 w-3 ml-1 cursor-pointer hover:text-red-500" 
                          onClick={() => quitarCapacidad(c.id)}
                        />
                      </Badge>
                    ))}
                  </div>
                )}
                
                <p className="text-xs text-gray-400 mt-2">
                  {capacidadesSeleccionadas.length} modelo(s) habilitado(s)
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notas">Notas</Label>
                <Textarea
                  id="notas"
                  placeholder="Notas adicionales..."
                  value={formData.notas}
                  onChange={(e) => setFormData({...formData, notas: e.target.value})}
                  rows={2}
                />
              </div>

              {/* Logo del taller */}
              <div className="space-y-2">
                <Label>Logo del Taller (PDF o imagen)</Label>
                <div className="flex gap-4 items-start">
                  <div className="flex-1">
                    <Input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setLogoFile(file);
                          if (file.type.startsWith('image/')) {
                            const reader = new FileReader();
                            reader.onload = (ev) => setLogoPreview(ev.target.result);
                            reader.readAsDataURL(file);
                          } else {
                            setLogoPreview(null);
                          }
                        }
                      }}
                    />
                    <p className="text-xs text-gray-500 mt-1">Formatos: PDF, JPG, PNG, WebP (máx 2MB)</p>
                  </div>
                  {(logoPreview || formData.logo_url) && (
                    <div className="relative">
                      <img 
                        src={logoPreview || formData.logo_url}
                        alt="Logo preview"
                        className="w-20 h-20 object-contain border rounded"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute -top-2 -right-2 h-6 w-6 bg-red-100 hover:bg-red-200"
                        onClick={() => {
                          setLogoFile(null);
                          setLogoPreview(null);
                          setFormData({...formData, logo_url: ''});
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-teal-500 hover:bg-teal-600" disabled={saving}>
                  {saving ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Guardando...</>
                  ) : (
                    editMode ? 'Guardar cambios' : 'Crear taller'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={showDetail} onOpenChange={setShowDetail}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedTaller?.razon_social}</DialogTitle>
            </DialogHeader>
            {selectedTaller && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500">Número ANAC</Label>
                    <p className="font-medium font-mono">{selectedTaller.numero_anac}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Nivel de Suscripción</Label>
                    <p>{getNivelBadge(selectedTaller.nivel_suscripcion)}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">País</Label>
                    <p>{selectedTaller.pais || selectedTaller.ubicacion?.pais}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Provincia</Label>
                    <p>{selectedTaller.provincia || selectedTaller.ubicacion?.provincia}</p>
                  </div>
                </div>
                
                {selectedTaller.direccion && (
                  <div>
                    <Label className="text-gray-500">Dirección</Label>
                    <p>{selectedTaller.direccion}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500">Email</Label>
                    <p>{selectedTaller.email || selectedTaller.titular_suscripcion?.email || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Teléfono</Label>
                    <p>{selectedTaller.telefono || selectedTaller.titular_suscripcion?.telefono || '-'}</p>
                  </div>
                </div>

                {selectedTaller.capacidades_anac?.length > 0 && (
                  <div>
                    <Label className="text-gray-500">Capacidades ANAC</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedTaller.capacidades_anac.map((cap, idx) => (
                        <Badge key={idx} variant="outline">{cap}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {selectedTaller.notas && (
                  <div>
                    <Label className="text-gray-500">Notas</Label>
                    <p className="text-sm">{selectedTaller.notas}</p>
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => { setShowDetail(false); openEditForm(selectedTaller); }}
                  >
                    <Edit className="mr-2 h-4 w-4" /> Editar
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 border-teal-300 text-teal-700 hover:bg-teal-50"
                    onClick={() => { setShowDetail(false); irAEnviarMensaje(selectedTaller); }}
                    data-testid="talleres-detail-enviar-mensaje"
                  >
                    <Send className="mr-2 h-4 w-4" /> Enviar mensaje
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminTalleres;
